import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useModal } from "@ebay/nice-modal-react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { ManagedUserGroupUpdateInput } from "types/generated";
import { getDrawerFormFix } from "utils/css";

import { getManagementStrategy } from "../../helpers";
import { UserManagementActivationStatus } from "../../types";
import { EditGroupFields } from "./types";
import useUpdateGroup from "../useUpdateGroup";
import { ManagedUserGroupView } from "../types";

type EditDrawerContentProps = {
  group: ManagedUserGroupView;
  activationStatus: UserManagementActivationStatus;
  setIsDirty: (isDirty: boolean) => void;
};

const EditDrawerContent = ({ group, activationStatus, setIsDirty }: EditDrawerContentProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const drawer = useModal();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationIdpGroupMapping,
    defaultCallbackTrackProperties: {
      managementStrategy: getManagementStrategy(activationStatus),
    },
  });

  const groupEditForm = useForm<EditGroupFields>({
    defaultValues: {
      description: group.description,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    register,
    formState: { isDirty },
  } = groupEditForm;

  const { onUpdate, loading } = useUpdateGroup();

  const onSubmit: SubmitHandler<EditGroupFields> = (formData) => {
    trackSegmentAnalyticsEvent("Update group");

    // Create the update input with description only
    const updateInput: ManagedUserGroupUpdateInput = {
      id: group.id,
      description: formData.description.trim(),
      accessRules: [], // TODO: [AAC] remove this when BE create new update/create mutations for role assignment
    };

    onUpdate(updateInput)
      .then(({ data }) => {
        if (data?.managedUserGroupUpdate) {
          reportSuccess({
            message: `Group description was updated`,
          });
          drawer.hide();
          trackSegmentAnalyticsEvent("Group details updated");
        }
      })
      .catch(onError);
  };

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  return (
    <FormProvider {...groupEditForm}>
      <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Edit details" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody fullHeight hasStickyFooter>
          <FormField
            label="IdP group ID"
            helperText="This is the group identifier in your IdP and cannot be changed"
          >
            {({ ariaInputProps }) => <Input value={group.groupName} disabled {...ariaInputProps} />}
          </FormField>

          <FormField label="Description" isOptional>
            {({ ariaInputProps }) => (
              <Textarea
                placeholder="Enter group description here..."
                maxRows={15}
                {...register("description")}
                {...ariaInputProps}
              />
            )}
          </FormField>

          <DrawerFooter sticky>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" loading={loading}>
                Save
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </DrawerBody>
      </form>
    </FormProvider>
  );
};

export default EditDrawerContent;
